import Component from '@glimmer/component';
import { service } from '@ember/service';
import Heading from 'tio-ui/components/heading';
import Drawer from 'tio-ui/components/drawer';
import { formatCurrency } from 'tio-ui/format';
import { t } from 'ember-intl';
import {
  PhCalendarDots,
  PhSealCheck,
  PhMoneyWavy,
  PhHourglassLow,
  PhCalendarBlank,
} from 'ember-phosphor-icons';
import { planRequiresLoanStandingVerification } from 'tio-employee/utils/slr-plan-eligibility';
import type { PlanExtension as PlanModel } from 'tio-data/data/resources/plan.ext';
import type IntlService from 'ember-intl/services/intl';
import type SessionContextService from 'tio-employee/services/session-context';
import type {
  LimitsDetailSignature,
  LimitsStatusDataSignature,
} from 'tio-common/services/tuition-assistance';

/**
 * BenefitDetailDrawer — View Benefit plan details in a right drawer
 *
 * Displays plan name, plan description, contribution start date,
 * contribution amount with cadence, waiting period (if applicable),
 * annual limit (if applicable), and lifetime limit (if applicable).
 *
 * All values are pulled from the API-backed PlanModel and the
 * current Employee. Rows for waiting period, annual limit, and
 * lifetime limit are only rendered when the API provides a
 * positive value for that field.
 *
 * @example
 * ```gts
 * <BenefitDetailDrawer
 *   @isOpen={{this.showBenefitDrawer}}
 *   @onClose={{this.closeBenefitDrawer}}
 *   @plan={{this.plan}}
 * />
 * ```
 */
interface BenefitDetailDrawerSignature {
  Args: {
    /** Whether the drawer is open */
    isOpen: boolean;
    /** Called when the drawer should close */
    onClose: () => void;
    /** The employee's plan model */
    plan: PlanModel | null | undefined;
    /** Employee-specific limits status for this benefit plan */
    limitsStatus?: LimitsStatusDataSignature;
  };
  Element: HTMLDivElement;
}

export default class BenefitDetailDrawer extends Component<BenefitDetailDrawerSignature> {
  @service declare intl: IntlService;
  @service declare sessionContext: SessionContextService;

  get plan() {
    return this.args.plan;
  }

  // SLR loan-standing plans carry `eligibility_conditions`; when present, show a
  // requirements section describing the periodic good-standing verification (#8718).
  get showLoanStandingRequirements() {
    return planRequiresLoanStandingVerification(this.plan?.eligibilityConditions);
  }

  get planName() {
    return this.plan?.name || this.intl.t('accounts.benefit_detail.default_plan_name');
  }

  get planDescription() {
    return this.plan?.description;
  }

  private isFiniteNumber(value: number | null | undefined): value is number {
    return typeof value === 'number' && Number.isFinite(value);
  }

  private isPositiveNumber(value: number | null | undefined): value is number {
    return this.isFiniteNumber(value) && value > 0;
  }

  get limitDetailsBySpecificity(): Array<LimitsDetailSignature | undefined> {
    const details = this.args.limitsStatus?.details;
    return [details?.scheme, details?.batch, details?.product, details?.company];
  }

  getLimitStatusValue(field: 'annual_limit' | 'limit') {
    // Select the most specific scope that explicitly provides a value. A
    // payload value of 0 is meaningful ("no limit") and must win over the
    // plan-level fallback, so accept any finite number here. The `> 0` check
    // in hasAnnualLimit/hasLifetimeLimit decides whether the row renders.
    for (const detail of this.limitDetailsBySpecificity) {
      const value = detail?.[field];
      if (this.isFiniteNumber(value)) {
        return value;
      }
    }
    return null;
  }

  get annualLimitCents() {
    return this.getLimitStatusValue('annual_limit') ?? this.plan?.annualLimit;
  }

  get lifetimeLimitCents() {
    return this.getLimitStatusValue('limit') ?? this.plan?.lifetimeLimit;
  }

  get hasWaitingPeriod() {
    const months = this.plan?.waitingPeriod;
    return months != null && months > 0;
  }

  get hasAnnualLimit() {
    return this.isPositiveNumber(this.annualLimitCents);
  }

  get hasLifetimeLimit() {
    return this.isPositiveNumber(this.lifetimeLimitCents);
  }

  get annualLimit() {
    const limit = this.annualLimitCents;
    if (limit == null) return '—';
    return formatCurrency(limit / 100);
  }

  get lifetimeLimit() {
    const limit = this.lifetimeLimitCents;
    if (limit == null) return '—';
    return formatCurrency(limit / 100);
  }

  // --- Contribution start date ---

  /**
   * Parses an API date string (typically `YYYY-MM-DD`) as a local
   * Date. Using `new Date('YYYY-MM-DD')` would be interpreted as
   * UTC midnight, which causes off-by-one display in negative
   * timezones — that was the source of the wrong start date.
   */
  private parseApiDate = (dateStr: string | undefined | null): Date | null => {
    if (!dateStr) return null;

    const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
    if (ymd) {
      const [, y, m, d] = ymd;
      const year = Number(y);
      const month = Number(m);
      const day = Number(d);
      const date = new Date(year, month - 1, day);

      if (isNaN(date.getTime())) return null;

      // Reject out-of-range values that JS would otherwise normalize
      // (e.g. 2024-13-40 → 2025-02-09).
      if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
        return null;
      }

      return date;
    }

    // Fallback for non-standard date strings (e.g. ISO-8601 with a
    // time component such as "2024-01-15T12:00:00Z"). Anything the
    // YYYY-MM-DD branch above couldn't parse — including full ISO
    // timestamps from the API — comes through here. We accept JS's
    // default `new Date(...)` parsing for these and only reject the
    // result when it produces NaN.
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? null : date;
  };

  /**
   * Resolves the contribution start date as a Date object based
   * on the plan's `milestoneDate`, sourcing the underlying date
   * from the appropriate API-backed Employee field. For
   * `_PLUS_WAIT` milestones, the plan's `waitingPeriod` and
   * `waitingPeriodUnit` are added to the base date.
   *
   * Returns null when no date can be sourced from the API
   * (e.g. `TODAY` or `FIRST_CONTRIBUTION` with no recorded
   * contribution yet) so the row can be hidden rather than
   * displaying a fabricated value.
   */
  get contributionStartDateValue() {
    const employee = this.sessionContext.currentEmployee;
    const milestone = this.plan?.milestoneDate?.toUpperCase();

    let baseDate: Date | null;

    switch (milestone) {
      case 'ELIGIBILITY_DATE':
      case 'ELIGIBILITY_DATE_PLUS_WAIT':
        baseDate = this.parseApiDate(employee?.eligibilityStartDate);
        break;
      case 'START_DATE':
      case 'START_DATE_PLUS_WAIT':
        baseDate = this.parseApiDate(employee?.employmentStartDate);
        break;
      case 'TODAY':
      case 'FIRST_CONTRIBUTION':
      default:
        // No reliable single date is exposed for these milestones
        // on the Employee/Plan models, so we don't fabricate one.
        return null;
    }

    if (!baseDate) return null;

    if (milestone?.endsWith('_PLUS_WAIT') && this.plan?.waitingPeriod) {
      const unit = this.plan.waitingPeriodUnit?.toLowerCase();
      if (unit === 'days') {
        baseDate.setDate(baseDate.getDate() + this.plan.waitingPeriod);
      } else {
        // Default to months (matches tio-payment behavior)
        baseDate.setMonth(baseDate.getMonth() + this.plan.waitingPeriod);
      }
    }

    return baseDate;
  }

  get contributionStartDate() {
    const d = this.contributionStartDateValue;
    if (!d) return '';

    return this.intl.formatDate(d, {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  }

  get cadenceUnit() {
    switch (this.plan?.cadence?.toUpperCase()) {
      case 'MONTHLY':
        return this.intl.t('accounts.benefit_detail.per_month');
      case 'QUARTERLY':
        return this.intl.t('accounts.benefit_detail.per_quarter');
      case 'SEMI_ANNUALLY':
        return this.intl.t('accounts.benefit_detail.per_semi_annual');
      case 'ANNUALLY':
        return this.intl.t('accounts.benefit_detail.per_year');
      default:
        return '';
    }
  }

  get paymentCadenceLabel() {
    switch (this.plan?.cadence?.toUpperCase()) {
      case 'MONTHLY':
        return this.intl.t('accounts.benefit_detail.cadence_monthly');
      case 'QUARTERLY':
        return this.intl.t('accounts.benefit_detail.cadence_quarterly');
      case 'SEMI_ANNUALLY':
        return this.intl.t('accounts.benefit_detail.cadence_semi_annually');
      case 'ANNUALLY':
        return this.intl.t('accounts.benefit_detail.cadence_annually');
      case 'NEVER':
        return this.intl.t('accounts.benefit_detail.cadence_none');
      default:
        return '';
    }
  }

  get milestoneDateLabel() {
    switch (this.plan?.milestoneDate?.toUpperCase()) {
      case 'TODAY':
        return this.intl.t('accounts.benefit_detail.milestone_today');
      case 'FIRST_CONTRIBUTION':
        return this.intl.t('accounts.benefit_detail.milestone_first_contribution');
      case 'ELIGIBILITY_DATE':
        return this.intl.t('accounts.benefit_detail.milestone_eligibility_date');
      case 'ELIGIBILITY_DATE_PLUS_WAIT':
        return this.intl.t('accounts.benefit_detail.milestone_eligibility_date_plus_wait');
      case 'START_DATE':
        return this.intl.t('accounts.benefit_detail.milestone_start_date');
      case 'START_DATE_PLUS_WAIT':
        return this.intl.t('accounts.benefit_detail.milestone_start_date_plus_wait');
      default:
        return '';
    }
  }

  /**
   * Number of contribution periods per year for the plan's cadence.
   */
  get periodsPerYear() {
    switch (this.plan?.cadence?.toUpperCase()) {
      case 'MONTHLY':
        return 12;
      case 'QUARTERLY':
        return 4;
      case 'SEMI_ANNUALLY':
        return 2;
      case 'ANNUALLY':
        return 1;
      default:
        return 12;
    }
  }

  /**
   * Whether the tier duration is already captured by annual or lifetime limits.
   * If true, don't show duration on the contribution amount row.
   * If false, show estimated end year instead.
   */
  get isTierDurationCapturedByLimits() {
    const tiers = this.plan?.tiers;
    if (!tiers?.length) return true;

    // Tiers are step functions keyed by the contribution cycle they start at,
    // so the schedule's span is the highest `fromCycle`. A schedule that never
    // advances past its first cycle has no duration to show.
    const sorted = [...tiers].sort((a, b) => (a.fromCycle || 0) - (b.fromCycle || 0));
    const totalCycles = Math.max(...sorted.map((tier) => tier.fromCycle || 0));
    if (totalCycles <= 1) return true;

    // Total contribution across the tiered span: each tier contributes its
    // amount for every cycle until the next tier starts (the final tier runs
    // through the last defined cycle). If that matches the lifetime limit, the
    // limit already conveys the cap and the duration is captured.
    let tierTotal = 0;
    for (let i = 0; i < sorted.length; i++) {
      const tier = sorted[i];
      const start = tier?.fromCycle || 0;
      const end = i < sorted.length - 1 ? sorted[i + 1]?.fromCycle || start : totalCycles + 1;
      tierTotal += (tier?.amount || 0) * Math.max(0, end - start);
    }
    if (this.plan?.lifetimeLimit && tierTotal === this.plan.lifetimeLimit) {
      return true;
    }

    // If the first tier's annualized amount matches the annual limit, the
    // duration is captured.
    const firstTier = sorted[0];
    if (firstTier && this.plan?.annualLimit) {
      const annualFromTier = (firstTier.amount || 0) * this.periodsPerYear;
      if (annualFromTier === this.plan.annualLimit) {
        return true;
      }
    }

    return false;
  }

  /**
   * Estimated end year from total tier periods,
   * anchored to the contribution start date. Returns null when no
   * start date can be resolved so callers can avoid showing an
   * "until" suffix anchored to today.
   */
  get estimatedEndYear() {
    const tiers = this.plan?.tiers;
    if (!tiers?.length) return null;

    // Tiers start at a given contribution cycle; the schedule runs through the
    // last tier's starting cycle, so its span is the highest `fromCycle`.
    const totalPeriods = Math.max(...tiers.map((tier) => tier.fromCycle || 0));
    if (totalPeriods <= 0) return null;

    const startDate = this.contributionStartDateValue;
    if (!startDate) return null;

    const cadence = this.plan?.cadence?.toUpperCase();
    let monthsPerPeriod = 1;
    if (cadence === 'QUARTERLY') monthsPerPeriod = 3;
    if (cadence === 'SEMI_ANNUALLY') monthsPerPeriod = 6;
    if (cadence === 'ANNUALLY') monthsPerPeriod = 12;

    const totalMonths = totalPeriods * monthsPerPeriod;
    const endDate = new Date(startDate.getFullYear(), startDate.getMonth() + totalMonths, 1);
    return endDate.getFullYear().toString();
  }

  /**
   * Formatted contribution amount including cadence, e.g. "$50/month".
   * Per spec:
   * - If tier duration IS captured by limits → just "$50/month"
   * - If tier duration is NOT captured by limits → "$50/month until ~2028"
   */
  get contributionWithCadence() {
    const tiers = this.plan?.tiers;
    if (!tiers?.length) return '—';
    const amount = tiers[0]?.amount;
    if (!amount) return '—';
    const formattedAmount = formatCurrency(amount / 100);
    const unit = this.cadenceUnit;
    if (!unit) return formattedAmount;

    let label = `${formattedAmount}/${unit}`;

    // If tier duration is NOT captured by limits, show estimated end year
    if (!this.isTierDurationCapturedByLimits && this.estimatedEndYear) {
      label += ` ${this.intl.t('accounts.benefit_detail.until_year', { year: this.estimatedEndYear })}`;
    }

    return label;
  }

  get waitingPeriodLabel() {
    const months = this.plan?.waitingPeriod;
    if (months == null || months === 0) return '—';
    if (months === 1) return this.intl.t('accounts.benefit_detail.employed_one_month');
    return this.intl.t('accounts.benefit_detail.employed_months', { months });
  }

  <template>
    <Drawer @isOpen={{@isOpen}} @onClose={{@onClose}} @size="lg" @placement="right" ...attributes>
      <:header>
        <div class="tio-col-tight">
          <Heading @level={{3}}>
            {{this.planName}}
          </Heading>
          {{#if this.planDescription}}
            <p class="leading-relaxed tio-fg-muted tio-text-base-medium">
              {{this.planDescription}}
            </p>
          {{/if}}
        </div>
      </:header>
      <:body>
        <div class="tio-col-flush gap-8 px-8 py-10">
          {{! Contribution Start Date — only if resolvable from API data }}
          {{#if this.contributionStartDate}}
            <div class="tio-row-between">
              <div class="tio-row-tight">
                <PhCalendarBlank @size={{20}} @weight="fill" class="tio-fg-secondary" />
                <span class="tio-fg-secondary tio-text-base-semibold">{{t
                    "accounts.benefit_detail.contribution_start_date"
                  }}</span>
              </div>
              <span class="tio-fg-muted tio-text-base-medium">{{this.contributionStartDate}}</span>
            </div>
          {{/if}}

          {{! Payment Cadence }}
          {{#if this.paymentCadenceLabel}}
            <div class="tio-row-between">
              <div class="tio-row-tight">
                <PhCalendarDots @size={{20}} @weight="fill" class="tio-fg-secondary" />
                <span class="tio-fg-secondary tio-text-base-semibold">{{t
                    "accounts.benefit_detail.payment_cadence"
                  }}</span>
              </div>
              <span class="tio-fg-muted tio-text-base-medium">{{this.paymentCadenceLabel}}</span>
            </div>
          {{/if}}

          {{! Milestone Date }}
          {{#if this.milestoneDateLabel}}
            <div class="tio-row-between">
              <div class="tio-row-tight">
                <PhCalendarBlank @size={{20}} @weight="fill" class="tio-fg-secondary" />
                <span class="tio-fg-secondary tio-text-base-semibold">{{t
                    "accounts.benefit_detail.milestone_date"
                  }}</span>
              </div>
              <span class="tio-fg-muted tio-text-base-medium">{{this.milestoneDateLabel}}</span>
            </div>
          {{/if}}

          {{! Contribution Amount with cadence (e.g. "$50/month") }}
          <div class="tio-row-between">
            <div class="tio-row-tight">
              <PhMoneyWavy @size={{20}} @weight="fill" class="tio-fg-secondary" />
              <span class="tio-fg-secondary tio-text-base-semibold">{{t
                  "accounts.benefit_detail.contribution_amount"
                }}</span>
            </div>
            <span class="tio-fg-muted tio-text-base-medium">{{this.contributionWithCadence}}</span>
          </div>

          {{! Waiting Period — only if applicable }}
          {{#if this.hasWaitingPeriod}}
            <div class="tio-row-between">
              <div class="tio-row-tight">
                <PhHourglassLow @size={{20}} @weight="fill" class="tio-fg-secondary" />
                <span class="tio-fg-secondary tio-text-base-semibold">{{t
                    "accounts.benefit_detail.waiting_period"
                  }}</span>
              </div>
              <span class="tio-fg-muted tio-text-base-medium">{{this.waitingPeriodLabel}}</span>
            </div>
          {{/if}}

          {{! Annual Limit — only if applicable }}
          {{#if this.hasAnnualLimit}}
            <div class="tio-row-between">
              <div class="tio-row-tight">
                <PhCalendarDots @size={{20}} @weight="fill" class="tio-fg-secondary" />
                <span class="tio-fg-secondary tio-text-base-semibold">{{t
                    "accounts.benefit_detail.annual_limit"
                  }}</span>
              </div>
              <span class="tio-fg-muted tio-text-base-medium">{{this.annualLimit}}</span>
            </div>
          {{/if}}

          {{! Lifetime Limit — only if applicable }}
          {{#if this.hasLifetimeLimit}}
            <div class="tio-row-between">
              <div class="tio-row-tight">
                <PhSealCheck @size={{20}} @weight="fill" class="tio-fg-secondary" />
                <span class="tio-fg-secondary tio-text-base-semibold">{{t
                    "accounts.benefit_detail.lifetime_limit"
                  }}</span>
              </div>
              <span class="tio-fg-muted tio-text-base-medium">{{this.lifetimeLimit}}</span>
            </div>
          {{/if}}

          {{#if this.showLoanStandingRequirements}}
            <div class="tio-col-tight" data-test-slr-loan-standing-requirements>
              <Heading @level={{4}}>{{t "slr.loan_standing.benefit_detail.heading"}}</Heading>
              <p class="tio-fg-muted tio-text-base-medium">
                {{t "slr.loan_standing.benefit_detail.description"}}
              </p>
              <span class="tio-fg-secondary tio-text-base-semibold">
                {{t "slr.loan_standing.benefit_detail.requirements_heading"}}
              </span>
              <ul class="list-disc pl-5 tio-fg-muted tio-text-base-medium">
                <li>{{t "slr.loan_standing.benefit_detail.requirement_borrower"}}</li>
                <li>{{t "slr.loan_standing.benefit_detail.requirement_servicer"}}</li>
                <li>{{t "slr.loan_standing.benefit_detail.requirement_balance"}}</li>
                <li>{{t "slr.loan_standing.benefit_detail.requirement_standing"}}</li>
              </ul>
              <p class="tio-fg-muted tio-text-sm-medium">
                {{t "slr.loan_standing.benefit_detail.note"}}
              </p>
            </div>
          {{/if}}

        </div>
      </:body>
    </Drawer>
  </template>
}
